import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import { HorizonDuration, HousePotentialProfile, PotentialLevel, ActivationLevel } from '../../types/ldaheV6';
import { calculateLDAHEResult, HOUSE_TITLES_EN, HOUSE_TITLES_HI } from '../../utils/v600/ldaheEngine';
import {
  Calendar,
  Clock,
  Compass,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Shield,
  Layers,
  ChevronRight,
  ChevronDown,
  Info,
  Flame,
  Zap,
  Target,
  Crown,
  BookOpen,
  Eye,
  Sliders,
  Award,
  Coins,
  Briefcase,
  Heart,
  Home,
  Globe,
  Activity,
  Send
} from 'lucide-react';

interface LifeActivationTimelineViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  onOpenConsultation?: (prompt?: string) => void;
}

export const LifeActivationTimelineView: React.FC<LifeActivationTimelineViewProps> = ({
  kundali,
  lang = 'en',
  chartStyle = 'north',
  focusDate = new Date(),
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  const [activeHorizon, setActiveHorizon] = useState<HorizonDuration>('current_antar');
  const [selectedHouseModal, setSelectedHouseModal] = useState<HousePotentialProfile | null>(null);
  const [activeTabSection, setActiveTabSection] = useState<'windows' | 'matrix' | 'combinations'>('windows');
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null);

  // Compute Engine Results deterministically
  const ldaheData = useMemo(() => {
    return calculateLDAHEResult(kundali, activeHorizon, focusDate, lang);
  }, [kundali, activeHorizon, focusDate, lang]);

  // Color helper functions
  const getPotentialBadge = (level: PotentialLevel) => {
    switch (level) {
      case 'very_strong':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'strong':
        return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'moderate':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'mild':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low':
        return 'bg-stone-100 text-stone-700 border-stone-300';
      case 'mixed':
        return 'bg-purple-100 text-purple-800 border-purple-300';
    }
  };

  const getActivationBadge = (level: ActivationLevel) => {
    switch (level) {
      case 'very_strong':
        return 'bg-rose-100 text-rose-800 border-rose-300 font-bold';
      case 'strong':
        return 'bg-orange-100 text-orange-800 border-orange-300 font-semibold';
      case 'moderate':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'mild':
        return 'bg-slate-100 text-slate-700 border-slate-300';
      case 'low':
        return 'bg-stone-100 text-stone-600 border-stone-300';
      case 'mixed':
        return 'bg-purple-100 text-purple-800 border-purple-300';
    }
  };

  const formatLevel = (level: string) => {
    switch (level) {
      case 'very_strong':
        return isHi ? 'अति प्रबल' : 'Very Strong';
      case 'strong':
        return isHi ? 'प्रबल' : 'Strong';
      case 'moderate':
        return isHi ? 'मध्यम' : 'Moderate';
      case 'mild':
        return isHi ? 'सौम्य' : 'Mild';
      case 'low':
        return isHi ? 'अल्प/सुप्त' : 'Low';
      case 'mixed':
        return isHi ? 'मिश्रित' : 'Mixed';
      default:
        return level;
    }
  };

  return (
    <div className="space-y-6">
      {/* =================================================================== */}
      {/* 1. HORIZON SELECTOR & ARCHITECTURAL DISCIPLINE BANNER                */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-astro-border/80 pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-astro-gold/20 text-astro-gold border border-astro-gold/30">
                LDAHE-V6.0 Engine
              </span>
              <h2 className="text-lg sm:text-xl font-bold text-astro-text font-serif">
                {isHi ? 'जीवन क्षेत्र सक्रियता एवं दशा कालक्रम' : 'Life Domain Activation & Astrological Timeline'}
              </h2>
            </div>
            <p className="text-xs text-astro-secondary-text max-w-2xl">
              {isHi
                ? 'जन्मजात सामर्थ्य (Natal Potential) → दशा-गोचर सक्रियता (Planetary Activation) → संगम (Dasha + Transit + Varga) → फलकथन'
                : 'Deterministic period-driven engine tracking Natal Potential → Planetary Activation → Convergence Windows → Domain Interpretations.'}
            </p>
          </div>

          {/* Astrological Period Activation & Canonical Product Horizons Selector */}
          <div className="flex flex-wrap items-center p-1 rounded-xl bg-astro-secondary-surface border border-astro-border shadow-2xs self-start lg:self-auto gap-1">
            <button
              onClick={() => setActiveHorizon('current_antar')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeHorizon === 'current_antar' || activeHorizon === '2y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>{isHi ? 'सक्रिय अंतर्दशा' : 'Active Antar'}</span>
            </button>
            <button
              onClick={() => setActiveHorizon('next_antar')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeHorizon === 'next_antar' || activeHorizon === '5y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>{isHi ? 'आगामी अंतर्दशा' : 'Next Antar'}</span>
            </button>
            <button
              onClick={() => setActiveHorizon('mahadasha_arc')}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                activeHorizon === 'mahadasha_arc' || activeHorizon === '10y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>{isHi ? 'संपूर्ण महादशा' : 'Full Mahadasha'}</span>
            </button>
            <div className="h-4 w-px bg-astro-border mx-0.5" />
            <button
              onClick={() => setActiveHorizon('30D')}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeHorizon === '30D'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              30D
            </button>
            <button
              onClick={() => setActiveHorizon('90D')}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeHorizon === '90D'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              90D
            </button>
            <button
              onClick={() => setActiveHorizon('1Y')}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeHorizon === '1Y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              1Y
            </button>
            <button
              onClick={() => setActiveHorizon('3Y')}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeHorizon === '3Y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              3Y
            </button>
            <button
              onClick={() => setActiveHorizon('10Y')}
              className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                activeHorizon === '10Y'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              10Y
            </button>
          </div>
        </div>

        {/* Executive Summary Card */}
        <div className="p-4 rounded-xl bg-astro-sage-tint/40 border border-astro-sage-tint flex flex-col sm:flex-row items-start gap-3">
          <Sparkles className="w-5 h-5 text-astro-accent shrink-0 mt-0.5" />
          <div className="space-y-1 w-full">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'दशा-गोचर सक्रियता सार संक्षेप' : 'Astrological Period Convergence Summary'}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-astro-secondary-text font-mono">
                {ldaheData.focusDateFormatted && (
                  <span className="px-2 py-0.5 rounded bg-white/80 border border-astro-border">
                    {isHi ? 'मूल्यांकन: ' : 'Focus: '}{ldaheData.focusDateFormatted} (Age {ldaheData.currentAge})
                  </span>
                )}
                {ldaheData.panchangaSummary && (
                  <span className="px-2 py-0.5 rounded bg-white/80 border border-astro-border">
                    {isHi ? `${ldaheData.panchangaSummary.varaNameHi}, ${ldaheData.panchangaSummary.tithiNameHi}` : `${ldaheData.panchangaSummary.varaNameEn}, ${ldaheData.panchangaSummary.tithiNameEn}`}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-astro-text leading-relaxed">
              {isHi ? ldaheData.executiveSummaryHi : ldaheData.executiveSummaryEn}
            </p>
          </div>
        </div>

        {/* Secondary Sub-Tabs */}
        <div className="flex items-center gap-2 pt-1 border-t border-astro-border/60">
          <button
            onClick={() => setActiveTabSection('windows')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTabSection === 'windows'
                ? 'bg-astro-primary text-white font-bold'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Target className="w-3.5 h-3.5" />
            <span>{isHi ? 'सक्रियता कालखंड (Windows)' : 'Activation Windows'}</span>
          </button>

          <button
            onClick={() => setActiveTabSection('matrix')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTabSection === 'matrix'
                ? 'bg-astro-primary text-white font-bold'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>{isHi ? '१२-भाव सामर्थ्य × सक्रियता आव्यूह' : '12-House Potential × Activation Matrix'}</span>
          </button>

          <button
            onClick={() => setActiveTabSection('combinations')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTabSection === 'combinations'
                ? 'bg-astro-primary text-white font-bold'
                : 'bg-white border border-astro-border text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{isHi ? 'शास्त्रीय भाव संयोग (2-6-10-11 आदि)' : 'Classical Combinations (2-6-10-11, 4-8-12)'}</span>
          </button>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 2. TAB SECTION A: ACTIVATION WINDOWS (CONVERGENCE TIMELINE)         */}
      {/* =================================================================== */}
      {activeTabSection === 'windows' && (
        <div className="space-y-6">
          {/* Domain Hierarchy Grid (Strongly, Moderately, Dormant) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Strongly Activated */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-emerald-600" />
                  {isHi ? 'अति सक्रिय क्षेत्र' : 'Strongly Activated'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800 font-bold">
                  {ldaheData.stronglyActivatedDomains.length} Domains
                </span>
              </div>
              <div className="space-y-2">
                {ldaheData.stronglyActivatedDomains.map((dom, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-emerald-50/60 border border-emerald-200/80 text-xs">
                    <div className="font-bold text-emerald-950 flex items-center justify-between">
                      <span>{isHi ? dom.nameHi : dom.nameEn}</span>
                      <span className="text-[10px] text-emerald-700 font-mono">H{dom.associatedHouses.join(', ')}</span>
                    </div>
                    <div className="text-[11px] text-emerald-800 mt-0.5">
                      Peak: {isHi ? dom.peakWindowHi : dom.peakWindowEn}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Moderately Activated */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-800 flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-indigo-600" />
                  {isHi ? 'मध्यम सक्रिय क्षेत्र' : 'Moderately Activated'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-800 font-bold">
                  {ldaheData.moderatelyActivatedDomains.length} Domains
                </span>
              </div>
              <div className="space-y-2">
                {ldaheData.moderatelyActivatedDomains.map((dom, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-indigo-50/60 border border-indigo-200/80 text-xs">
                    <div className="font-bold text-indigo-950 flex items-center justify-between">
                      <span>{isHi ? dom.nameHi : dom.nameEn}</span>
                      <span className="text-[10px] text-indigo-700 font-mono">H{dom.associatedHouses.join(', ')}</span>
                    </div>
                    <div className="text-[11px] text-indigo-800 mt-0.5">
                      Window: {isHi ? dom.peakWindowHi : dom.peakWindowEn}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dormant / Unactivated with Scientific Vedic Clarification */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-700 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-stone-500" />
                  {isHi ? 'सुप्त / अप्रभावित क्षेत्र' : 'Dormant / Unactivated'}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-700 font-bold">
                  {ldaheData.dormantDomains.length} Domains
                </span>
              </div>
              <div className="space-y-2">
                {ldaheData.dormantDomains.map((dom, i) => (
                  <div key={i} className="p-2.5 rounded-lg bg-stone-50 border border-stone-200 text-xs">
                    <div className="font-semibold text-stone-800 flex items-center justify-between">
                      <span>{isHi ? dom.nameHi : dom.nameEn}</span>
                      <span className="text-[10px] text-stone-500 font-mono">H{dom.associatedHouses.join(', ')}</span>
                    </div>
                    <div className="text-[10px] text-stone-600 mt-0.5 italic">
                      {isHi ? dom.summaryHi : dom.summaryEn}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Protective Pedagogical Note on Dormancy */}
          <div className="p-3.5 rounded-xl bg-amber-50/80 border border-amber-200/90 text-xs text-amber-950 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              <strong>{isHi ? 'शास्त्रीय नियम: ' : 'Vedic Principle: '}</strong>
              {isHi
                ? '"सुप्त / अप्रभावित क्षेत्र" का अर्थ यह नहीं है कि कोई घटना नहीं घटेगी। इसका अर्थ है कि इस कालखंड में ग्रहों की प्रमुख दशा व गोचर का संकेंद्रण अन्य प्राथमिक विषयों पर केंद्रित है।'
                : '"No significant activation detected" does not mean that nothing will happen. It indicates the current astrological indicators do not exhibit a high concentration of evidence for that domain within this specific horizon.'}
            </p>
          </div>

          {/* Chronological Activation Windows List */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-astro-text uppercase tracking-wider flex items-center gap-2">
              <Calendar className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'गहन सक्रियता कालखंड एवं शिखर कालक्रम' : 'Convergence Activation Windows & Peak Dates'}</span>
            </h3>

            {ldaheData.windows.map((w, index) => {
              const isExpanded = selectedWindowId === w.id || (selectedWindowId === null && index === 0);
              return (
                <div
                  key={w.id}
                  className="bg-astro-card border border-astro-border rounded-2xl shadow-xs overflow-hidden transition-all"
                >
                  {/* Window Header */}
                  <div
                    onClick={() => setSelectedWindowId(isExpanded ? '' : w.id)}
                    className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-astro-secondary-surface/40 transition-colors"
                  >
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-mono font-bold bg-astro-primary text-white">
                          {w.startDate} – {w.endDate}
                        </span>
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-mono font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                          <Flame className="w-3 h-3 text-amber-600" />
                          <span>Peak: {w.peakDate}</span>
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${
                          w.overallQuality === 'opportunity' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' :
                          w.overallQuality === 'supportive' ? 'bg-teal-50 text-teal-800 border-teal-300' :
                          w.overallQuality === 'transformative' ? 'bg-purple-50 text-purple-800 border-purple-300' :
                          'bg-blue-50 text-blue-800 border-blue-300'
                        }`}>
                          Quality: {w.overallQuality.toUpperCase()}
                        </span>
                      </div>
                      <h4 className="text-base font-bold text-astro-text font-serif">
                        {isHi ? w.windowLabelHi : w.windowLabelEn}
                      </h4>
                      <p className="text-xs text-astro-secondary-text">
                        {isHi ? w.primaryThemesHi : w.primaryThemesEn}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right hidden sm:block">
                        <div className="text-[11px] text-astro-muted">{isHi ? 'विश्वास स्तर' : 'Confidence'}</div>
                        <div className="text-xs font-bold text-emerald-700 capitalize">
                          {w.confidence.replace('_', ' ')}
                        </div>
                      </div>
                      <div className="p-1.5 rounded-lg bg-astro-secondary-surface border border-astro-border text-astro-primary">
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>

                  {/* Window Expanded Details */}
                  {isExpanded && (
                    <div className="p-4 sm:p-6 border-t border-astro-border bg-astro-secondary-surface/20 space-y-5">
                      {/* Dasha & Transit Convergence Evidence */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div className="p-3 rounded-xl bg-white border border-astro-border space-y-1 shadow-2xs">
                          <span className="font-bold text-astro-primary flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-astro-accent" />
                            {isHi ? 'दशा एवं प्रत्यंतर संरेखण' : 'Vimshottari Dasha Alignment'}
                          </span>
                          <p className="text-astro-secondary-text">
                            {isHi ? w.activeDashaSummaryHi : w.activeDashaSummaryEn}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-white border border-astro-border space-y-1 shadow-2xs">
                          <span className="font-bold text-astro-primary flex items-center gap-1.5">
                            <Compass className="w-3.5 h-3.5 text-astro-accent" />
                            {isHi ? 'ग्रह गोचर एवं दृष्टि संगम' : 'Transit & Drishti Convergence'}
                          </span>
                          <p className="text-astro-secondary-text">
                            {isHi ? w.activeTransitsSummaryHi : w.activeTransitsSummaryEn}
                          </p>
                        </div>
                      </div>

                      {/* Domain Event Narrative */}
                      <div className="p-4 rounded-xl bg-white border border-astro-border space-y-2 shadow-2xs">
                        <span className="text-xs font-bold uppercase tracking-wider text-astro-primary flex items-center gap-1.5">
                          <Sparkles className="w-4 h-4 text-astro-accent" />
                          {isHi ? 'जीवन क्षेत्र फलकथन एवं फलित निष्कर्ष' : 'Domain & Event Interpretation'}
                        </span>
                        <p className="text-xs sm:text-sm text-astro-text leading-relaxed">
                          {isHi ? w.domainNarrativeHi : w.domainNarrativeEn}
                        </p>
                      </div>

                      {/* Activated Houses Table & Potential vs Activation */}
                      <div className="space-y-2">
                        <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                          {isHi ? 'भाव प्रभाव एवं जन्मजात सामर्थ्य विश्लेषण' : 'House Involvement & Natal vs Transit Influence'}
                        </span>
                        <div className="grid grid-cols-1 gap-2">
                          {w.activatedHouses.map(h => (
                            <div
                              key={h.houseNumber}
                              className="p-3 rounded-xl bg-white border border-astro-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs shadow-2xs"
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-astro-text font-serif">
                                    {isHi ? HOUSE_TITLES_HI[h.houseNumber] : HOUSE_TITLES_EN[h.houseNumber]}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-[10px] border ${getPotentialBadge(h.natalPotentialLevel)}`}>
                                    Natal: {formatLevel(h.natalPotentialLevel)}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded text-[10px] border ${getActivationBadge(h.level)}`}>
                                    Activation: {formatLevel(h.level)}
                                  </span>
                                </div>
                                <p className="text-[11px] text-astro-secondary-text">
                                  {isHi ? h.potentialVsActivationVerdictHi : h.potentialVsActivationVerdictEn}
                                </p>
                              </div>

                              <button
                                onClick={() => {
                                  const profile = ldaheData.natalPotentialProfiles.find(p => p.houseNumber === h.houseNumber);
                                  if (profile) setSelectedHouseModal(profile);
                                }}
                                className="px-2.5 py-1 rounded-md bg-astro-secondary-surface text-astro-primary hover:bg-astro-sage-tint text-[11px] font-semibold border border-astro-border shrink-0 self-start sm:self-auto cursor-pointer"
                              >
                                {isHi ? 'भाव प्रमाण देखें' : 'Inspect House Proof'}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Classical Combination Formation */}
                      {w.combinationPatterns.length > 0 && (
                        <div className="p-3.5 rounded-xl bg-purple-50/70 border border-purple-200 text-xs space-y-1.5">
                          <div className="font-bold text-purple-950 flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5 text-purple-700" />
                            <span>{isHi ? w.combinationPatterns[0].nameHi : w.combinationPatterns[0].nameEn}</span>
                          </div>
                          <p className="text-purple-900 text-[11px] leading-relaxed">
                            {isHi ? w.combinationPatterns[0].interpretationHi : w.combinationPatterns[0].interpretationEn}
                          </p>
                        </div>
                      )}

                      {/* Strategic Do's and Don'ts */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
                        <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 space-y-2">
                          <span className="font-bold text-emerald-950 flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                            {isHi ? 'अनुशंसित कर्म (Strategic Do\'s)' : 'Strategic Do\'s'}
                          </span>
                          <ul className="space-y-1 text-[11px] text-emerald-900">
                            {(isHi ? w.strategicDosHi : w.strategicDosEn).map((d, i) => (
                              <li key={i} className="flex items-start gap-1.5">
                                <span className="text-emerald-600 font-bold">•</span>
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200 space-y-2">
                          <span className="font-bold text-amber-950 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                            {isHi ? 'सावधानियां (Strategic Don\'ts)' : 'Strategic Don\'ts'}
                          </span>
                          <ul className="space-y-1 text-[11px] text-amber-900">
                            {(isHi ? w.strategicDontsHi : w.strategicDontsEn).map((d, i) => (
                              <li key={i} className="flex items-start gap-1.5">
                                <span className="text-amber-600 font-bold">•</span>
                                <span>{d}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Consult Acharya on this window */}
                      {onOpenConsultation && (
                        <div className="pt-2 flex justify-end">
                          <button
                            onClick={() => onOpenConsultation(`Please explain my life activation during ${w.startDate} to ${w.endDate} with primary focus on ${w.primaryThemesEn}.`)}
                            className="px-4 py-2 rounded-xl bg-astro-primary text-white text-xs font-bold shadow-xs hover:opacity-90 transition-all flex items-center gap-2 cursor-pointer"
                          >
                            <Send className="w-3.5 h-3.5" />
                            <span>{isHi ? 'इस कालखंड पर आचार्य से परामर्श करें' : 'Discuss this Window with Acharya'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 2. TAB SECTION B: 12-HOUSE POTENTIAL × ACTIVATION MATRIX            */}
      {/* =================================================================== */}
      {activeTabSection === 'matrix' && (
        <div className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border/80 pb-3">
            <div>
              <h3 className="text-base font-bold text-astro-text font-serif flex items-center gap-2">
                <Sliders className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? '१२-भाव जन्मजात सामर्थ्य × बहु-क्षितिज सक्रियता आव्यूह' : '12-House Potential × Multi-Horizon Activation Matrix'}</span>
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'प्रत्येक भाव का जन्मजात बल (Natal Potential) एवं २, ५ तथा १०-वर्षीय समयावधि में उसकी सक्रियता का विश्लेषण'
                  : 'Independent evaluation of Natal Potential vs future 2-Year, 5-Year, and 10-Year planetary activations.'}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-astro-border bg-astro-secondary-surface/60 text-astro-primary font-bold">
                  <th className="py-2.5 px-3">{isHi ? 'भाव' : 'House'}</th>
                  <th className="py-2.5 px-3">{isHi ? 'राशि व भावेश' : 'Sign & Lord'}</th>
                  <th className="py-2.5 px-3">{isHi ? 'जन्मजात सामर्थ्य' : 'Natal Potential'}</th>
                  <th className="py-2.5 px-3 text-center">{isHi ? '२Y सक्रियता' : '2Y Act.'}</th>
                  <th className="py-2.5 px-3 text-center">{isHi ? '५Y सक्रियता' : '5Y Act.'}</th>
                  <th className="py-2.5 px-3 text-center">{isHi ? '१०Y सक्रियता' : '10Y Act.'}</th>
                  <th className="py-2.5 px-3">{isHi ? 'प्रमुख वर्ग (D-Chart)' : 'Key Varga'}</th>
                  <th className="py-2.5 px-3 text-right">{isHi ? 'क्रिया' : 'Action'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border/60">
                {ldaheData.matrixRows.map(row => (
                  <tr key={row.houseNumber} className="hover:bg-astro-secondary-surface/30 transition-colors">
                    <td className="py-3 px-3 font-bold text-astro-text font-serif">
                      H{row.houseNumber}
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-semibold text-astro-text">{row.signName}</div>
                      <div className="text-[10px] text-astro-muted">Lord: {row.lordPlanet}</div>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded text-[11px] border ${getPotentialBadge(row.natalPotential)}`}>
                        {formatLevel(row.natalPotential)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] border ${getActivationBadge(row.activation2Y)}`}>
                        {formatLevel(row.activation2Y)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] border ${getActivationBadge(row.activation5Y)}`}>
                        {formatLevel(row.activation5Y)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] border ${getActivationBadge(row.activation10Y)}`}>
                        {formatLevel(row.activation10Y)}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-0.5 rounded bg-astro-secondary-surface border border-astro-border text-[10px] font-mono text-astro-primary">
                        {row.keyVarga}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => {
                          const profile = ldaheData.natalPotentialProfiles.find(p => p.houseNumber === row.houseNumber);
                          if (profile) setSelectedHouseModal(profile);
                        }}
                        className="px-2.5 py-1 rounded bg-astro-primary text-white text-[11px] font-bold shadow-2xs hover:opacity-90 transition-all cursor-pointer"
                      >
                        {isHi ? 'प्रमाण' : 'Forensics'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 2. TAB SECTION C: CLASSICAL COMBINATIONS (2-6-10-11, etc.)          */}
      {/* =================================================================== */}
      {activeTabSection === 'combinations' && (
        <div className="bg-astro-card border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
          <div className="border-b border-astro-border/80 pb-3">
            <h3 className="text-base font-bold text-astro-text font-serif flex items-center gap-2">
              <Layers className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'शास्त्रीय बहु-भाव संयोग (Classical House Combinations)' : 'Classical Multi-House Convergence Formations'}</span>
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'पराशर प्रणाली के अनुसार केवल एक भाव नहीं, बल्कि संबंधित भावों का चतुष्कोण अथवा त्रिकोण मिलकर विशिष्ट जीवन घटनाओं का निर्माण करता है।'
                : 'Parashari principles dictate that events manifest through combinatorial house networks rather than isolated bhava activations.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ldaheData.classicalCombinationFormations.map(pat => (
              <div
                key={pat.id}
                className="p-4 rounded-xl bg-white border border-astro-border shadow-2xs space-y-2 hover:border-astro-primary transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm text-astro-primary font-serif">
                    {isHi ? pat.nameHi : pat.nameEn}
                  </span>
                  <div className="flex items-center gap-1">
                    {pat.houses.map(h => (
                      <span key={h} className="px-1.5 py-0.5 rounded bg-astro-secondary-surface text-[10px] font-mono font-bold text-astro-text border border-astro-border">
                        H{h}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-astro-secondary-text leading-relaxed">
                  {isHi ? pat.interpretationHi : pat.interpretationEn}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* 3. MODAL: DEEP FORENSIC HOUSE PROOF (D9, D10, D7, D4 & SHASTRA)     */}
      {/* =================================================================== */}
      {selectedHouseModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-astro-card border border-astro-border rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-astro-border pb-3">
              <div className="space-y-0.5">
                <span className="text-[10px] uppercase tracking-wider font-bold text-astro-gold">
                  {isHi ? 'गहन शास्त्रीय भाव निरीक्षण' : 'Deep Astrological Bhava Forensic'}
                </span>
                <h3 className="text-lg font-bold text-astro-text font-serif">
                  {isHi ? HOUSE_TITLES_HI[selectedHouseModal.houseNumber] : HOUSE_TITLES_EN[selectedHouseModal.houseNumber]}
                </h3>
              </div>
              <button
                onClick={() => setSelectedHouseModal(null)}
                className="p-1.5 rounded-lg bg-astro-secondary-surface text-astro-muted hover:text-astro-text cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Natal Potential Rating */}
            <div className="p-3.5 rounded-xl bg-astro-sage-tint/30 border border-astro-sage-tint flex items-center justify-between">
              <div className="space-y-0.5">
                <span className="text-xs text-astro-secondary-text">{isHi ? 'जन्मजात सामर्थ्य स्तर' : 'Natal Potential Grade'}</span>
                <div className="font-bold text-sm text-astro-primary capitalize">
                  {formatLevel(selectedHouseModal.potentialLevel)} ({selectedHouseModal.potentialScore}/100)
                </div>
              </div>
              <span className={`px-3 py-1 rounded-md text-xs font-bold border ${getPotentialBadge(selectedHouseModal.potentialLevel)}`}>
                {selectedHouseModal.potentialLevel.toUpperCase()}
              </span>
            </div>

            {/* Evidence List */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'ज्योतिषीय साक्ष्य एवं ग्रह स्थिति' : 'Astrological Evidence & Factors'}
              </span>
              <ul className="space-y-1.5 text-xs text-astro-text bg-white p-3.5 rounded-xl border border-astro-border">
                {(isHi ? selectedHouseModal.evidenceHi : selectedHouseModal.evidenceEn).map((ev, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{ev}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Divisional Charts Confirmation (D9, D10, D7, D4) */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'सूक्ष्म वर्ग कुंडली पुष्टि (D9, D10, D7, D4)' : 'Harmonic Varga Confirmations (D9, D10, D7, D4)'}
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                {selectedHouseModal.vargaConfirmations.map((v, i) => (
                  <div key={i} className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-astro-primary font-mono">{v.varga}</span>
                      <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${v.isDignified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                        {v.isDignified ? 'Confirmed' : 'Moderate'}
                      </span>
                    </div>
                    <p className="text-[11px] text-astro-secondary-text">
                      {isHi ? v.noteHi : v.noteEn}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Classical Sutra Citation */}
            <div className="p-3 rounded-xl bg-amber-50/70 border border-amber-200 text-xs text-amber-950 font-serif italic">
              {isHi ? selectedHouseModal.sutrasHi : selectedHouseModal.sutrasEn}
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedHouseModal(null)}
                className="px-4 py-2 rounded-xl bg-astro-primary text-white text-xs font-bold cursor-pointer"
              >
                {isHi ? 'बंद करें' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
